import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import {
  Wallet, Upload, CheckCircle2, Clock, FileText, Loader2, Plus, Receipt, Trash2, Download, AlertTriangle
} from "lucide-react";
import { BrandMark } from "@/lib/branding";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function StatItem({ label, value, tone = "default" }) {
  const tones = {
    default: "text-foreground",
    good:    "text-emerald-300",
    warn:    "text-amber-300",
  };
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold">{label}</div>
      <div className={`font-display text-3xl mt-1 ${tones[tone]}`}>{value}</div>
    </div>
  );
}

export default function PublicDAF() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  // Add-payment form state
  const [pAmount, setPAmount] = useState("");
  const [pKind, setPKind] = useState("kickstart");
  const [pMethod, setPMethod] = useState("Transferência");
  const [pReceivedAt, setPReceivedAt] = useState("");
  const [pReceipt, setPReceipt] = useState("");
  const [pNotes, setPNotes] = useState("");
  const [pName, setPName] = useState("");
  const [saving, setSaving] = useState(false);

  // Invoice upload state
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/public/daf/${token}`);
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Portal não encontrado ou expirado.");
    }
  };

  useEffect(() => { load(); }, [token]);

  const submitPayment = async () => {
    const amount = parseFloat(pAmount);
    if (!amount || amount <= 0) { toast.error("Valor inválido"); return; }
    setSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/public/daf/${token}/payments`, {
        kind: pKind, amount, method: pMethod,
        received_at: pReceivedAt || null,
        receipt_reference: pReceipt || null,
        notes: pNotes || null,
        confirmed_by_name: pName || "DAF",
      });
      toast.success("Pagamento confirmado.");
      setPAmount(""); setPReceipt(""); setPNotes("");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao confirmar pagamento");
    } finally { setSaving(false); }
  };

  const uploadInvoice = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${BACKEND_URL}/api/public/daf/${token}/invoice`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Fatura carregada.");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao carregar fatura");
    } finally { setUploading(false); }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <div className="text-[hsl(var(--warning))] mb-4"><BrandMark /></div>
          <h1 className="font-display text-3xl mb-2">Portal indisponível</h1>
          <p className="text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[hsl(var(--warning))]" />
      </div>
    );
  }

  const d = data.deposit || {};
  const plan = d.plan || {};
  const payments = d.payments || [];
  const kickstartPaid = Boolean(d.kickstart_paid);
  const fullyPaid = Boolean(d.fully_paid);
  const remainingKick = Math.max(0, (d.kickstart_target || 0) - (d.kickstart_paid_amount || 0));
  const remainingTotal = Math.max(0, (d.total_target || 0) - (d.total_paid_amount || 0));
  const invoice = d.invoice;

  const billing = data.billing || {};
  const billed = billing.target === "beneficiary" ? (data.beneficiaries || [])[billing.beneficiary_index || 0] || {}
                : billing.target === "family" ? data.family_responsible || {}
                : billing.other_party || {};

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="daf-portal">
      {/* Header */}
      <header className="bg-card border-b border-border">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 flex items-center justify-between">
          <BrandMark />
          <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Portal DAF</div>
        </div>
      </header>

      {/* Client + billing */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 pt-12 pb-6">
        <div className="text-[10px] uppercase tracking-[0.25em] text-[hsl(var(--warning))] font-semibold mb-3">
          Cliente para faturação
        </div>
        <h1 className="font-display text-4xl lg:text-5xl mb-6 leading-tight inline-flex items-center gap-3">
          <Receipt className="w-9 h-9 text-[hsl(var(--warning))]" /> {data.customer_name || "Cliente"}
        </h1>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-card border border-border rounded-2xl p-5" data-testid="billing-details">
            <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold mb-2">Faturar a</div>
            <div className="font-ui text-lg mb-1">{billed.name || "—"}</div>
            <div className="text-sm text-foreground leading-relaxed">
              <div>NIF: {billed.nif || "—"}</div>
              <div>{billed.address || ""}</div>
              <div>{billed.postal_code} {billed.locality}</div>
              <div>{billed.email || ""}</div>
              <div>{billed.phone || ""}</div>
            </div>
          </div>
          <div className="bg-card border border-border rounded-2xl p-5">
            <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold mb-3">Plano</div>
            <div className="space-y-3">
              <StatItem label="Caução total" value={`${(plan.total_amount || 0).toFixed(2)}€`} />
              <StatItem label="Arranque recrutamento" value={`${(plan.kickstart_amount || 0).toFixed(2)}€`} tone="warn" />
              <StatItem label="Data prevista de início" value={plan.service_start_date || "—"} />
            </div>
          </div>
        </div>
      </section>

      {/* Progress cards */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className={`rounded-2xl p-5 border ${
            kickstartPaid ? "bg-emerald-500/10 border-emerald-500/30" : "bg-amber-500/10 border-amber-500/30"
          }`} data-testid="progress-kickstart">
            <div className="flex items-center gap-2 mb-2">
              {kickstartPaid ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <Clock className="w-4 h-4 text-amber-400" />}
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">Arranque de recrutamento</div>
            </div>
            <div className="font-display text-2xl">
              {(d.kickstart_paid_amount || 0).toFixed(2)}€
              <span className="text-muted-foreground text-lg"> / {(d.kickstart_target || 0).toFixed(2)}€</span>
            </div>
            <div className={`text-xs mt-2 ${kickstartPaid ? "text-emerald-300" : "text-amber-300"}`}>
              {kickstartPaid
                ? "Recrutamento desbloqueado ✓"
                : `Falta ${remainingKick.toFixed(2)}€ · Deadline: 24h após assinatura`}
            </div>
          </div>
          <div className={`rounded-2xl p-5 border ${
            fullyPaid ? "bg-emerald-500/10 border-emerald-500/30" : "bg-background border-border"
          }`} data-testid="progress-total">
            <div className="flex items-center gap-2 mb-2">
              {fullyPaid ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <Clock className="w-4 h-4 text-muted-foreground" />}
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">Caução total</div>
            </div>
            <div className="font-display text-2xl">
              {(d.total_paid_amount || 0).toFixed(2)}€
              <span className="text-muted-foreground text-lg"> / {(d.total_target || 0).toFixed(2)}€</span>
            </div>
            <div className={`text-xs mt-2 ${fullyPaid ? "text-emerald-300" : "text-muted-foreground"}`}>
              {fullyPaid ? "Cliente 100% em ordem ✓" : `Falta ${remainingTotal.toFixed(2)}€ · Deadline: 48h antes do arranque do serviço`}
            </div>
          </div>
        </div>
      </section>

      {/* Add payment form */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        <div className="bg-card border border-border rounded-2xl p-4 sm:p-6 lg:p-8">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[hsl(var(--warning))] font-semibold mb-3">Registar pagamento</div>
          <h2 className="font-display text-2xl mb-6 inline-flex items-center gap-2">
            <Plus className="w-5 h-5 text-[hsl(var(--warning))]" /> Confirmar valor recebido do cliente
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
            <label className="block">
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-1">Tipo</div>
              <select value={pKind} onChange={(e) => setPKind(e.target.value)}
                className="w-full bg-background border border-border rounded-xl px-3 py-2 text-foreground text-sm"
                data-testid="payment-kind">
                <option value="kickstart">Arranque de recrutamento</option>
                <option value="remainder">Remanescente</option>
                <option value="adjustment">Ajuste / Extra</option>
              </select>
            </label>
            <label className="block">
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-1">Valor (€)</div>
              <input type="number" step="0.01" value={pAmount} onChange={(e) => setPAmount(e.target.value)}
                placeholder="Ex: 400"
                className="w-full bg-background border border-border rounded-xl px-3 py-2 text-foreground text-sm placeholder:text-muted-foreground/70"
                data-testid="payment-amount" />
            </label>
            <label className="block">
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-1">Método</div>
              <select value={pMethod} onChange={(e) => setPMethod(e.target.value)}
                className="w-full bg-background border border-border rounded-xl px-3 py-2 text-foreground text-sm"
                data-testid="payment-method">
                <option>Transferência</option>
                <option>MB Way</option>
                <option>Numerário</option>
                <option>Cheque</option>
                <option>Multibanco</option>
              </select>
            </label>
            <label className="block">
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-1">Data recebida</div>
              <input type="date" value={pReceivedAt} onChange={(e) => setPReceivedAt(e.target.value)}
                className="w-full bg-background border border-border rounded-xl px-3 py-2 text-foreground text-sm"
                data-testid="payment-received-at" />
            </label>
            <label className="block">
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-1">Ref. comprovativo</div>
              <input value={pReceipt} onChange={(e) => setPReceipt(e.target.value)}
                placeholder="MB1234 / IBAN"
                className="w-full bg-background border border-border rounded-xl px-3 py-2 text-foreground text-sm placeholder:text-muted-foreground/70"
                data-testid="payment-ref" />
            </label>
            <label className="block">
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-1">Nome (quem confirma)</div>
              <input value={pName} onChange={(e) => setPName(e.target.value)}
                placeholder="Ex: Ana DAF"
                className="w-full bg-background border border-border rounded-xl px-3 py-2 text-foreground text-sm placeholder:text-muted-foreground/70"
                data-testid="payment-name" />
            </label>
          </div>
          <label className="block mb-3">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-1">Notas</div>
            <textarea rows={2} value={pNotes} onChange={(e) => setPNotes(e.target.value)}
              placeholder="Observações (opcional)"
              className="w-full bg-background border border-border rounded-xl px-3 py-2 text-foreground text-sm placeholder:text-muted-foreground/70"
              data-testid="payment-notes" />
          </label>
          <div className="flex justify-end">
            <button onClick={submitPayment} disabled={saving}
              className="bg-[hsl(var(--warning))] hover:bg-[hsl(var(--warning))] text-foreground font-semibold rounded-full px-6 py-2.5 text-sm inline-flex items-center gap-2 disabled:opacity-50"
              data-testid="payment-submit">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wallet className="w-4 h-4" />}
              Confirmar pagamento
            </button>
          </div>
        </div>
      </section>

      {/* Payments log */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        <div className="bg-card border border-border rounded-2xl p-6">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[hsl(var(--warning))] font-semibold mb-3">Histórico de pagamentos</div>
          {payments.length === 0 ? (
            <div className="text-sm text-muted-foreground italic" data-testid="payments-empty">Sem pagamentos registados.</div>
          ) : (
            <div className="space-y-2" data-testid="payments-log">
              {payments.map((p, i) => (
                <div key={p.id || i} className="flex items-center justify-between bg-background border border-border rounded-xl px-4 py-3 text-sm">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] uppercase tracking-[0.15em] font-semibold px-2 py-0.5 rounded-full ${
                        p.kind === "kickstart" ? "bg-amber-500/20 text-amber-300 border border-amber-500/30"
                        : p.kind === "remainder" ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30"
                        : "bg-background text-muted-foreground border border-border"
                      }`}>{p.kind}</span>
                      <span className="font-ui">{p.amount?.toFixed(2)}€</span>
                      <span className="text-muted-foreground text-xs">· {p.method}</span>
                    </div>
                    <div className="text-muted-foreground text-xs mt-1">
                      {p.confirmed_by_name || "DAF"} · {p.confirmed_at ? new Date(p.confirmed_at).toLocaleString("pt-PT") : ""}
                      {p.receipt_reference ? ` · Ref: ${p.receipt_reference}` : ""}
                      {p.received_at ? ` · Recebido ${p.received_at}` : ""}
                    </div>
                    {p.notes && <div className="text-muted-foreground text-xs italic mt-1">{p.notes}</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Invoice upload */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-6 pb-16">
        <div className="bg-card border border-border rounded-2xl p-6">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[hsl(var(--warning))] font-semibold mb-3">Fatura da caução</div>
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="flex-1">
              {invoice ? (
                <div className="text-sm" data-testid="invoice-info">
                  <div className="flex items-center gap-2 text-emerald-300 font-ui">
                    <FileText className="w-4 h-4" /> {invoice.filename}
                  </div>
                  <div className="text-muted-foreground text-xs mt-1">
                    {new Date(invoice.uploaded_at).toLocaleString("pt-PT")} · {(invoice.size / 1024).toFixed(0)} KB
                    {invoice.zoho_attached ? " · Sincronizada com Zoho ✓" : " · Aguarda sync Zoho"}
                  </div>
                  <a
                    href={`${BACKEND_URL}/api/public/daf/${token}/invoice`}
                    target="_blank" rel="noreferrer"
                    className="text-xs text-[hsl(var(--warning))] underline mt-1 inline-flex items-center gap-1"
                    data-testid="invoice-download"
                  >
                    <Download className="w-3 h-3" /> Descarregar
                  </a>
                </div>
              ) : (
                <div className="text-sm text-muted-foreground" data-testid="invoice-empty">
                  Ainda sem fatura carregada.
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <input ref={fileRef} type="file" accept="application/pdf" className="hidden"
                onChange={(e) => uploadInvoice(e.target.files?.[0])}
                data-testid="invoice-input" />
              <button
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="bg-[hsl(var(--warning))] hover:bg-[hsl(var(--warning))] text-foreground font-semibold rounded-md px-4 py-2 text-sm inline-flex items-center gap-2 disabled:opacity-50"
                data-testid="invoice-upload-btn">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {invoice ? "Substituir fatura" : "Carregar fatura"}
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
